import { api, bucketApi } from "@/lib/axios";
import type {
  ApiResponse,
  BrandConfigDto,
  BrandDetail,
  BrandUploadResult,
  BrandUploadResultDto,
  ReqDeleteBrand,
  ReqUploadBrandLogo,
  ReqUpsertBrand,
} from "../types/brand.types";

function toBrandDetail(dto: BrandConfigDto): BrandDetail {
  return {
    idx: dto.idx,
    brandId: dto.brandId ?? "",
    companyGuid: dto.companyGuid,
    companyName: dto.companyName,
    name: dto.name,
    host: dto.host,
    domains: dto.domains ?? [],
    ceoName: dto.ceoName,
    privacyOfficer: dto.privacyOfficer,
    bizNo: dto.bizNo,
    licenseNo: dto.licenseNo,
    address: dto.address,
    addressDetail: dto.addressDetail,
    phone: dto.phone,
    phoneDigits: dto.phoneDigits,
    copyright: dto.copyright,
    metaTitle: dto.metaTitle,
    metaDescription: dto.metaDescription,
    proposalFileName: dto.proposalFileName,
    proposalUrl: dto.proposalUrl,
    logoHeaderUrl: dto.logoHeaderUrl,
    logoAuthUrl: dto.logoAuthUrl,
    logoAuthInvert: dto.logoAuthInvert,
    faviconUrl: dto.faviconUrl,
    ogImageUrl: dto.ogImageUrl,
    orderMemoTag: dto.orderMemoTag,
    isActive: dto.isActive,
    showQuickOrder: dto.showQuickOrder ?? true,
    quickOrderPricePolicy:
      dto.quickOrderPricePolicy === "CONSULTATION" ? "CONSULTATION" : "STANDARD",
    showRegister: dto.showRegister ?? true,
    deliveryCategories: dto.deliveryCategories,
  };
}

function toUploadResult(dto: BrandUploadResultDto): BrandUploadResult {
  const s3Key = dto.s3Key ?? dto.S3Key;
  const fileGuid = dto.fileGuid ?? dto.FileGuid ?? null;
  if (!fileGuid?.trim() && !s3Key?.trim()) {
    throw new Error("업로드 응답에 저장 참조값이 없습니다.");
  }

  return {
    fileGuid,
    publicUrl: dto.publicUrl ?? dto.PublicUrl ?? "",
    s3Key: s3Key ?? "",
  };
}

const freshGetConfig = () => ({
  params: { _t: Date.now() },
  headers: {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});

export const listBrands = async (): Promise<BrandDetail[]> => {
  const { data } = await api.get<ApiResponse<BrandConfigDto[]>>(
    "/api/Brand/Admin/List",
    freshGetConfig(),
  );
  if (data.code !== 200) throw new Error(data.message);
  return data.result.map(toBrandDetail);
};

export const getBrand = async (
  idx: number,
): Promise<BrandDetail | null> => {
  const { data } = await api.get<ApiResponse<BrandConfigDto | null>>(
    `/api/Brand/Admin/by-idx/${idx}`,
    freshGetConfig(),
  );
  if (data.code !== 200) throw new Error(data.message);
  return data.result ? toBrandDetail(data.result) : null;
};

export const upsertBrand = async (req: ReqUpsertBrand): Promise<void> => {
  const { data } = await api.post<ApiResponse<null>>(
    "/api/Brand/Admin/Upsert",
    req,
  );
  if (data.code !== 200) throw new Error(data.message);
};

export const deleteBrand = async (req: ReqDeleteBrand): Promise<void> => {
  const { data } = await api.post<ApiResponse<null>>(
    "/api/Brand/Admin/Delete",
    req,
  );
  if (data.code !== 200) throw new Error(data.message);
};

export const uploadBrandLogo = async (
  req: ReqUploadBrandLogo,
): Promise<BrandUploadResult> => {
  const formData = new FormData();
  formData.append("File", req.file);
  formData.append("BrandId", req.BrandId);
  if (req.CompanyGuid?.trim()) {
    formData.append("CompanyGuid", req.CompanyGuid.trim());
  }
  formData.append("Purpose", req.Purpose);
  formData.append("ReqAuthGuid", req.ReqAuthGuid);

  const { data } = await bucketApi.post<ApiResponse<BrandUploadResultDto>>(
    "/api/File/brand/upload-asset",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  if (data.code !== 200) throw new Error(data.message);
  return toUploadResult(data.result);
};
